/**
 * @file Album related helper functions.
 */

import moment from 'moment';
import get from 'lodash.get';
import flatten from 'lodash.flatten';
import uniqBy from 'lodash.uniqby';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';

import { capitalize } from './text';
import {
  ALBUM_DATE_STATUS,
  ALBUM_SUBTYPE,
  ALBUM_HIGHLIGHTS_FILTER,
  ALBUM_TYPE,
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../constants/albums';
import getConfig from './config';
import { isHumanJoinCode } from './join-code';
import {
  ALBUM_FOLDER_TAG_TYPE,
  ALBUM_SPORTS_GAME_VENUE_TYPE,
} from '../constants/album-folder-tags';
import { CURRENCY } from '../constants/currency';
import { DATE_FORMAT_SIMPLE } from '../constants/dates';
import { getRosterIdentitySelfiePhoto } from './identity';
import { EVENT_ADDON_ITEM_TYPE } from '../constants/event-items';

// eslint-disable-next-line no-underscore-dangle
const isAlbum = album => get(album, '__typename', '') === 'Album';
const isAlbumPhotoGroup = album =>
  get(album, '__typename', '') === 'AlbumPhotoGroup';

const albumHasPhotoGroups = album =>
  album &&
  album.album_photo_groups &&
  album.album_photo_groups.records &&
  album.album_photo_groups.records.length > 0;

const hasFolderSubscription = album => {
  if (!album || !album.subscription_album_folder) {
    return false;
  }

  return !!album.subscription_album_folder.price.value;
};

const hasPpaPrice = album => {
  return !!(album && album.price_whole_album && album.price_whole_album.value);
};

const isPpa = album => {
  return hasPpaPrice(album) || hasFolderSubscription(album);
};

const isPpd = album => {
  return (
    !!get(album, 'default_price_per_photo') ||
    !!get(album, 'photo_pricing', null) ||
    get(album, 'album_print_packages.total', 0) > 0
  );
};

/**
 * Returns whether an album is individual or not.
 *
 * @param {Object} album The Album.
 * @returns {boolean}
 */
const isAlbumIndividual = album => {
  if (!album) {
    return false;
  }

  // Check all fields to have our bases covered in case
  // queries lack data.
  return (
    album.root_album_folder?.individual_albums ||
    album.album_folder?.individual_albums ||
    // Legacy individual album support (no parent):
    (typeof album.album_folder !== 'undefined' && !album.album_folder)
  );
};

/**
 * Gets an account object (as returned by Me query) and combines
 * all me.albums.records with all me.album_memberships.records.album.
 *
 * @param {Object} account - `me` object as returned by me query.
 * @param {Function} albumFilterFn - Optional album filtering function.
 * @returns {Array.<T>} - Albums.
 */
const joinUserAndMembershipAlbums = (account, albumFilterFn) => {
  const albumsFromMemberships = get(
    account,
    'album_memberships.records',
    [],
  ).map(record => record.album || []);

  const allAlbums = get(account, 'albums.records', []).concat(
    albumsFromMemberships,
  );

  return allAlbums.filter(album =>
    albumFilterFn ? albumFilterFn(album) : album,
  );
};

const getAlbumTypeNameFromEnum = type => {
  return type ? type.split('_').map(capitalize).join(' ') : 'General';
};

/**
 * Checks if a Web Gallery album allows photo purchases or not.
 * Checks for either Print purchases or Digital download purchases
 * by passing the appropriate flag.
 *
 * @param {object} album - The album structure as returned from GQL with web gallery params.
 * @param {boolean} [forPrint] - Check for print purchases.
 * @returns {boolean} - Whether the album allows photo purchases (print or download).
 */
const allowsPhotoPurchase = (album, forPrint) => {
  if (!album) {
    return false;
  }

  if (forPrint) {
    return album.web_gallery_print_purchases_enabled;
  }

  return !!get(album, 'web_gallery_price_per_photo.value', null);
};

/*
 * Checks if the provided Web Gallery album allows for free downloads
 *
 * @param {object} album - The album as provided by GQL with the web gallery fields
 * @returns {boolean} - Whether the album allows free downloads
 */
const allowsFreeDownloads = album => {
  if (!album || !album.web_gallery_price_per_photo) {
    return false;
  }

  return !get(album, 'web_gallery_price_per_photo.value', null);
};

/**
 * Returns whether or not the user is allowed to download a photo without
 * a watermark. IsEntitled is used for purchased photos so that they can make
 * their own choices.
 *
 * @param {Object} album The album to which the photo belongs to.
 * @param {Boolean} isEntitled Whether or not a user has the rights to this photo.
 * @returns {Boolean} Whether or not the user has the option to toggle watermark.
 */
const allowsDownloadWatermarkOption = (album, isEntitled) => {
  return (
    album &&
    album.watermark_config &&
    (album.download_without_watermark || isEntitled)
  );
};

/**
 * Returns if an album allows a user to share a photo
 * on social networks.
 *
 * @param {object} album - The album as provided by GQL with the web gallery fields.
 * @returns {boolean} - If this album allows social sharing.
 */
const socialShareEnabled = album => get(album, 'social_share_enabled', false);

const allowsPrintWatermarkOption = album => {
  return album && album.watermark_config && album.print_without_watermark;
};

/**
 * Returns, based on start & end date, whether the album is
 * current,past or future. Will return null if any of the start or
 * end date are missing from the album,.
 *
 * @param {Object} album The album to get the time status from.
 */
const albumDateStatus = album => {
  if (!album.start_date) {
    return ALBUM_DATE_STATUS.NA;
  }

  const startDate = moment(album.start_date).startOf('day');
  const endDate = album.end_date
    ? moment(album.end_date).startOf('day')
    : moment(album.start_date).endOf('day');

  const now = moment(getConfig().date.currentDate || moment()).startOf('day');

  const nowDiffStart = now.diff(startDate, 'days');
  const nowDiffEnd = now.diff(endDate, 'days');

  if (nowDiffEnd > 0) {
    return ALBUM_DATE_STATUS.PAST;
  }

  if (nowDiffEnd <= 0 && nowDiffStart >= 0) {
    return ALBUM_DATE_STATUS.PRESENT;
  }

  return ALBUM_DATE_STATUS.FUTURE;
};

const albumFilterByDateStatus = (albums, dateStatus) =>
  albums.filter(
    album => dateStatus === 'ALL' || albumDateStatus(album) === dateStatus,
  );

/**
 * Returns whether an album is purchasable or not.
 * For an album to be purchasable it needs to have a join code
 * and some kind of price.
 *
 * @param {Object} album The album.
 * @returns {boolean} Whether the album can be purchased or not.
 */
const isAlbumPurchasable = album => {
  return !!get(album, 'join_code') && (isPpd(album) || isPpa(album));
};

/**
 * Returns whether an album can be discovered or not.
 * For an album to be discovered it needs to have a human join code.
 *
 * @param {Object} album The album.
 * @returns {boolean} Whether the album can be discovered or not.
 */
const isAlbumDiscoverable = album => {
  if (!album) {
    return false;
  }

  return isHumanJoinCode(album.join_code);
};

/**
 * Returns photo groups that have photos.
 *
 * @param {Array<PhotoGroup>} photoGroups
 * @returns {Array<PhotoGroup>?} The list of PhotoGroups that have photos.
 */
const getPhotoGroupsWithPhotos = photoGroups => {
  return (
    photoGroups && photoGroups.filter(photoGroup => photoGroup.photos.total > 0)
  );
};

/**
 * Given an album with the `Album.selected_identities`
 * it produces the member (selected
 * identity) data required to populate the album members table in
 * the Album Membership view.
 *
 * @param {object} album The album with its members as returned from GQL.
 * @returns {Array} The unique, flattened member rows.
 */
const getAlbumUniqueSelectedIdentityRows = album => {
  if (!album || !album.selected_identities) {
    return [];
  }

  // Get all selected identities
  const rows = album.selected_identities.records.map(selectedIdentity => ({
    membershipId: selectedIdentity.album_membership.id,
    deleted: selectedIdentity.album_membership.is_deleted,
    ...selectedIdentity,
  }));

  // Flatten the selected identities into a single array
  // and filter out invitees and identities without selfies
  const flattenedRows = flatten(rows);
  const filteredRows = flattenedRows.filter(
    row => !row.from_invite && !!get(row, 'identity.current_matching_photo'),
  );

  return uniqBy(filteredRows, row => row.identity.id);
};

/**
 * Given an list of selected Identities produces the member (selected
 * identity) data required to populate the album members table in
 * the Album Membership view.
 *
 * @param {object} selectedIdentities The album with its members as returned from GQL.
 * @returns {Array} The unique, flattened member rows.
 */
const getSelectedIdentityRowsFromSelectedIdentities = (
  selectedIdentities = [],
) => {
  // Get all nested selected identities from each membership record
  const rows = selectedIdentities.map(selectedIdentity => ({
    membershipId: selectedIdentity.album_membership.id,
    deleted: selectedIdentity.album_membership.is_deleted,
    ...selectedIdentity,
  }));

  // filter out invitees and identities without selfies
  const filteredRows = rows.filter(row => !row.from_invite);

  return filteredRows;
};

/**
 * Returns true whenever the album requires
 * to be purchased before it can be created.
 *
 * @param {ALBUM_SUBTYPE} subtype The subtype of the album / folder.
 */
const createAlbumRequiresPurchase = subtype => {
  return [ALBUM_SUBTYPE.EVENTS, ALBUM_SUBTYPE.WEDDINGS].includes(subtype);
};

/**
 * Determines if an album can be deleted without asking for confirmation.
 *
 * @param {Album} album Album to check if we have permission to delete.
 */
const requiresConfirmationToDelete = album => {
  if (!album) {
    return false;
  }

  // All albums require confirmation to delete even if empty.
  return true;
};

/**
 * Determines if an album requires green screen modal check
 *
 * @param {Album} album Album to check
 */
const requiresGreenScreenCheck = album => {
  if (!album) {
    return false;
  }

  if (!album?.root_album_folder?.green_screen_enabled) {
    return false;
  }

  if (album?.photos?.total > 0 || album?.photos?.records?.length > 0) {
    return false;
  }

  // Avoids dependency cycle.
  const isYearbookEntity = !!(
    album?.root_album_folder?.pricing_categories?.records ?? []
  ).find(cat =>
    (cat?.pricing_category?.name ?? '').includes('Including Yearbook Export'),
  );

  // As of now we only auto generate albums for yearbook entities.
  // For the rest, the questions will be asked up front on create.
  if (!isYearbookEntity) {
    return false;
  }

  return true;
};

/**
 * Returns the first AlbumAlbumFolderTag of type GRADE found on an album.
 *
 * @param {Array} albumAlbumFolderTags - The album's AlbumAlbumFolderTags.
 * @returns {AlbumAlbumFolderTag|undefined} - The AlbumAlbumFolderTag.
 */
const getGradeAlbumAlbumFolderTag = (albumAlbumFolderTags = []) => {
  return albumAlbumFolderTags.find(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type === ALBUM_FOLDER_TAG_TYPE.GRADE,
  );
};

/**
 * Returns the first AlbumAlbumFolderTag of type UNIFORM found on an album.
 *
 * @param {Array} albumAlbumFolderTags - The album's AlbumAlbumFolderTags.
 * @returns {AlbumAlbumFolderTag|undefined} - The AlbumAlbumFolderTag.
 */
const getUniformAlbumAlbumFolderTag = (albumAlbumFolderTags = []) => {
  return albumAlbumFolderTags.find(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.UNIFORM,
  );
};

/**
 * Returns all AlbumAlbumFolderTags of type CUSTOM found on an album.
 *
 * @param {Array} albumAlbumFolderTags - The album's AlbumAlbumFolderTags.
 * @returns {Array<AlbumAlbumFolderTag>} - An array of AlbumAlbumFolderTags.
 */
const getCustomAlbumAlbumFolderTags = (albumAlbumFolderTags = []) => {
  return albumAlbumFolderTags.filter(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.CUSTOM,
  );
};

/**
 * "Updates" custom AlbumAlbumFolder tags by replacing them.
 *
 * @param {Object} options - The method's options.
 * @param {String} options.albumId - The album id.
 * @param {Array<Object>} options.albumAlbumFolderTags - The array of existing tags on the album.
 * @param {Array<String>} [options.customTagIds] - The Custom AlbumFolderTag ids to be updated.
 * @param {Function} options.createTags - The create tags mutation.
 * @param {Function} options.deleteTags - The delete tags mutation.
 * @returns {Promise<undefined>}
 */
const updateCustomAlbumAlbumFolderTags = async ({
  albumId,
  albumAlbumFolderTags,
  customTagIds = [],
  createTags,
  deleteTags,
}) => {
  const customTagRecords = albumAlbumFolderTags.filter(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.CUSTOM,
  );
  const albumFolderTagIds = customTagRecords.map(
    tag => tag.album_folder_tag.id,
  );
  const tagsEqual =
    customTagIds.every(id => albumFolderTagIds.includes(id)) &&
    customTagRecords.length === customTagIds.length;

  if (customTagIds.length > 0 && tagsEqual) {
    return;
  }

  // Delete all records and re-add what is necessary
  if (albumAlbumFolderTags.length > 0) {
    await deleteTags(customTagRecords.map(({ id }) => id));
  }

  if (customTagIds.length > 0) {
    await createTags(
      albumId,
      customTagIds.map(id => ({ id })),
    );
  }
};

const getOrCreateWhiteJerseysForTeams = async ({
  levelId,
  teamIds,
  createJerseys,
}) => {
  let homeJerseyId = null;
  let awayJerseyId = null;

  const {
    data: {
      uniformAlbumFolderTagLevelJerseysCreate: {
        uniform_album_folder_tag_level: currentLevel,
      },
    },
  } = await createJerseys({ levelId, jerseys: [] });

  const whiteHomeJersey = (currentLevel?.jerseys?.records ?? []).find(
    jersey =>
      jersey.colors?.[0] === 'White' &&
      jersey.name === 'White (Default)' &&
      jersey.venue === ALBUM_SPORTS_GAME_VENUE_TYPE.HOME &&
      jersey.team.id === teamIds[0],
  );

  const createJerseyRecords = [];

  if (whiteHomeJersey) {
    homeJerseyId = whiteHomeJersey.id;
  } else {
    createJerseyRecords.push({
      team_id: teamIds[0],
      colors: ['White'],
      name: 'White (Default)',
      venue: ALBUM_SPORTS_GAME_VENUE_TYPE.HOME,
    });
  }

  if (teamIds.length > 1) {
    const whiteAwayJersey = (currentLevel?.jerseys?.records ?? []).find(
      jersey =>
        jersey.colors?.[0] === 'White' &&
        jersey.name === 'White (Default)' &&
        jersey.venue === ALBUM_SPORTS_GAME_VENUE_TYPE.AWAY &&
        jersey.team.id === teamIds[1],
    );

    if (whiteAwayJersey) {
      awayJerseyId = whiteAwayJersey.id;
    } else {
      createJerseyRecords.push({
        team_id: teamIds[1],
        colors: ['White'],
        name: 'White (Default)',
        venue: ALBUM_SPORTS_GAME_VENUE_TYPE.AWAY,
      });
    }
  }

  if (createJerseyRecords.length > 0) {
    const {
      data: {
        uniformAlbumFolderTagLevelJerseysCreate: {
          uniform_album_folder_tag_level: updatedLevel,
        },
      },
    } = await createJerseys({ levelId, jerseys: createJerseyRecords });

    if (!homeJerseyId) {
      const newWhiteHomeJersey = (updatedLevel?.jerseys?.records ?? []).find(
        jersey =>
          jersey.colors?.[0] === 'White' &&
          jersey.name === 'White (Default)' &&
          jersey.venue === ALBUM_SPORTS_GAME_VENUE_TYPE.HOME,
      );

      homeJerseyId = newWhiteHomeJersey.id;
    }

    if (!awayJerseyId && teamIds.length > 1) {
      const newWhiteAwayJersey = (updatedLevel?.jerseys?.records ?? []).find(
        jersey =>
          jersey.colors?.[0] === 'White' &&
          jersey.name === 'White (Default)' &&
          jersey.venue === ALBUM_SPORTS_GAME_VENUE_TYPE.AWAY,
      );

      awayJerseyId = newWhiteAwayJersey.id;
    }
  }

  return [awayJerseyId, homeJerseyId].filter(Boolean);
};

/**
 * "Updates" uniform AlbumAlbumFolder tags by replacing them.
 *
 * @param {Object} options - The method's options.
 * @param {String} options.albumId - The album id.
 * @param {Array<Object>} options.albumAlbumFolderTags - The array of existing tags on the album.
 * @param {String} [options.tagId] - The Uniform AlbumFolderTag to be updated/deleted.
 * @param {String} [options.levelId] - The Album Folder Tag Level to be updated/deleted.
 * @param {Array} [options.levelIds] - The Album Folder Tag Levels to be associated
 * @param {Function} options.createTags - The create tags mutation.
 * @param {Function} options.deleteTags - The delete tags mutation.
 * @returns {Promise<undefined>}
 */
const updateUniformAlbumAlbumFolderTagUnique = async options => {
  const {
    albumId,
    albumAlbumFolderTags = [],
    tagId,
    levelId,
    levelIds,
    teamIds = [],
    jerseyIds = [],
    createTags,
    deleteTags,
    createJerseys,
    shouldCreateBaseJerseysForTeams = false,
  } = options;
  const uniformTagRecords = albumAlbumFolderTags.filter(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.UNIFORM,
  );

  let validatedJerseyIds = jerseyIds;

  if (
    teamIds?.length > 0 &&
    validatedJerseyIds?.length === 0 &&
    shouldCreateBaseJerseysForTeams &&
    createJerseys
  ) {
    validatedJerseyIds = await getOrCreateWhiteJerseysForTeams({
      levelId,
      teamIds,
      createJerseys,
    });
  }

  const getLevelData = levelItem => ({
    uniform_album_folder_tag_level_id: levelItem.levelId,
    uniform_album_folder_tag_level_jersey_ids: levelItem.jerseyIds
      .filter(Boolean)
      // Reverse here because the user creates "Team 1" first and "Team 2"
      // second (so Team 2 is return first from GQL) and we need consistency
      // with the labels, i.e. what the user selected for Team 1 should always come
      // first in the response after creation.
      .reverse(),
  });

  const levelsInput = (() => {
    if (levelIds?.length > 0) {
      return levelIds.map(id => getLevelData({ levelId: id, jerseyIds: [] }));
    }

    if (levelId) {
      return getLevelData({ levelId, jerseyIds: validatedJerseyIds });
    }

    return undefined;
  })();

  // Simply delete all tags and re-add them
  if (deleteTags) {
    await deleteTags(uniformTagRecords.map(({ id }) => id));
  }

  if (tagId) {
    await createTags(albumId, [
      {
        id: tagId,
        levels: levelsInput,
      },
    ]);
  }

  return undefined;
};

/**
 * Manages the update of an AlbumAlbumFolderTag. This is required because
 * for tags of type UNIFORM or GRADE we only allow a single tag per type
 * to exist on an album. That means that we must check for the existence
 * of other similarly typed tags and remove them when updating.
 *
 * @param {Object} options - The method's options.
 * @param {String} options.albumId - The album id.
 * @param {Array<Object>} options.albumAlbumFolderTags - The array of existing tags on the album.
 * @param {String} options.tagId - The album folder tag ID to be associated with the album.
 * @param {Function} options.createTags - The create tags mutation.
 * @param {Function} options.deleteTags - The delete tags mutation.
 * @returns {Promise<void>}
 */
const updateAlbumAlbumFolderGradeTag = async ({
  albumId,
  albumAlbumFolderTags = [],
  tagId,
  createTags,
  deleteTags,
}) => {
  const gradeTagRecords = albumAlbumFolderTags.filter(
    albumAlbumFolderTag =>
      albumAlbumFolderTag.album_folder_tag.type === ALBUM_FOLDER_TAG_TYPE.GRADE,
  );
  const existingAlbumAlbumFolderTag = gradeTagRecords.find(
    albumAlbumFolderTag => albumAlbumFolderTag.album_folder_tag.id === tagId,
  );

  // If the provided tagId exists as AlbumAlbumFolderTag do nothing.
  if (existingAlbumAlbumFolderTag) {
    return undefined;
  }

  // If !tagId remove all existing tags of the type provided
  // as it means we want to delete the tag (without replacing it with another).
  if (!tagId && gradeTagRecords.length > 0) {
    return deleteTags(gradeTagRecords.map(({ id }) => id));
  }

  // If the provided tagId does not exist as an AlbumAlbumFolderTag
  // create it
  if (tagId && !existingAlbumAlbumFolderTag) {
    const tasks = [createTags(albumId, [{ id: tagId }])];

    // If there are other tags by this type remove them as we allow only
    // one per type for UNIFORM and GRADE tags.
    if (gradeTagRecords.length > 0) {
      tasks.push(deleteTags(gradeTagRecords.map(({ id }) => id)));
    }

    await Promise.all(tasks);
  }

  return undefined;
};

/**
 * Determines if the given set of albumAlbumFolderTags include a set of unconfigured team jerseys
 *
 * @param {Array} albumAlbumFolderTags the album albumFolderTags
 * @returns {boolean}
 */
const hasUnconfiguredTeamJerseys = albumAlbumFolderTags => {
  if (!albumAlbumFolderTags || (albumAlbumFolderTags?.length ?? []) === 0) {
    return false;
  }

  const uniformAlbumAlbumFolderTag = getUniformAlbumAlbumFolderTag(
    albumAlbumFolderTags,
  );

  if (
    !uniformAlbumAlbumFolderTag ||
    !uniformAlbumAlbumFolderTag?.album_folder_tag?.uniform_enabled
  ) {
    return false;
  }

  const [uniformTagLevel] =
    uniformAlbumAlbumFolderTag?.uniform_album_folder_tag_levels.records ?? [];

  if (!uniformTagLevel) {
    return false;
  }

  const uniformTagLevelJerseys =
    uniformTagLevel?.uniform_album_folder_tag_level_jerseys.records ?? [];

  return !!uniformTagLevelJerseys.find(jersey =>
    jersey.name.includes('(Default)'),
  );
};

/**
 * Determines if the given set of albumFolderTags include a team tag
 *
 * @param {Array} albumFolderTags the albumFolderTags
 * @returns {boolean}
 */
const hasTeamTag = albumFolderTags => {
  if (!albumFolderTags || (albumFolderTags?.length ?? []) === 0) {
    return false;
  }

  const customAlbumFolderTags = albumFolderTags.filter(
    tag => tag.type === ALBUM_FOLDER_TAG_TYPE.CUSTOM,
  );

  if (customAlbumFolderTags.length === 0) {
    return false;
  }

  return !!customAlbumFolderTags.find(tag => tag.name === 'TEAM');
};

/**
 * Retrieves the team tag from albumFolderTags
 *
 * @param {Array} albumFolderTags the albumFolderTags
 * @returns {Object}
 */
const getTeamTag = albumFolderTags => {
  if (!albumFolderTags || (albumFolderTags?.length ?? []) === 0) {
    return undefined;
  }

  const customAlbumFolderTags = albumFolderTags.filter(
    tag => tag.type === ALBUM_FOLDER_TAG_TYPE.CUSTOM,
  );

  if (customAlbumFolderTags.length === 0) {
    return undefined;
  }

  return customAlbumFolderTags.find(tag => tag.name === 'TEAM');
};

/**
 * Gets album's assigned print packages by cross-referencing
 * their IDs to the account's print packages.
 *
 * @param {Array} printPackages - The account's print packages.
 * @param {Array} albumPrintPackages - Album's print package ids.
 * @returns {Array} - The album's assigned print package details.
 */
const getAlbumAssignedPrintPackages = (printPackages, albumPrintPackages) => {
  if (!printPackages.length || !albumPrintPackages.length) {
    return [];
  }

  return printPackages.filter(printPackage =>
    albumPrintPackages.find(
      albumPrintPackage =>
        albumPrintPackage.print_package.id === printPackage.id,
    ),
  );
};

/**
 * Gets album's un-assigned print packages by cross-referencing
 * their IDs to the account's print packages.
 *
 * @param {Array} printPackages - The account's print packages.
 * @param {Array} albumPrintPackageIds - Album's print package ids.
 * @returns {Array} - The album's assigned print package details.
 */
const getAlbumUnassignedPrintPackages = (
  printPackages,
  albumPrintPackageIds = [],
) => {
  if (!printPackages.length) {
    return [];
  }

  return printPackages.filter(
    printPackage => !albumPrintPackageIds.includes(printPackage.id),
  );
};

/**
 * Gets album's assigned custom prints by cross-referencing
 * their IDs to the account's custom prints.
 *
 * @param {Array} customPrints - The account's custom prints.
 * @param {Array} albumCustomPrints - Album's custom print items.
 * @returns {Array} - The album's assigned custom prints details.
 */
const getAlbumAssignedCustomPrints = (customPrints, albumCustomPrints) => {
  if (!customPrints.length || !albumCustomPrints.length) {
    return [];
  }

  return customPrints.filter(customPrint =>
    albumCustomPrints.find(
      albumCustomPrint => albumCustomPrint.custom_print.id === customPrint.id,
    ),
  );
};

/**
 * Gets album's un-assigned custom prints by cross-referencing
 * their IDs to the account's custom prints.
 *
 * @param {Array} customPrints - The account's custom prints.
 * @param {Array} albumCustomPrintItems - Album's custom print items. The array of existing tags on the album.
 * @param {String} [albumCustomPrintItems.customPrintId] - The custom print id number.
 * @returns {Array} - The album's assigned custom print details.
 */
const getAlbumUnassignedCustomPrints = (
  customPrints,
  albumCustomPrintItems = [],
) => {
  if (!customPrints.length) {
    return [];
  }

  const albumCustomPrintIds = albumCustomPrintItems.map(
    ({ customPrintId }) => customPrintId,
  );

  return customPrints.filter(
    customPrint => !albumCustomPrintIds.includes(customPrint.id),
  );
};

/**
 * "Updates" Album custom prints.
 *
 * @param {Object} options - The method's options.
 * @param {String} options.album - The album.
 * @param {Array<Object>} options.albumCustomPrintItems - The array of albumCustomPrintItems.
 * @param {Function} options.albumCustomPrintCreate - The create album custom print.
 * @param {Function} options.albumCustomPrintUpdate - The update album custom print mutation.
 * @param {Function} options.albumCustomPrintDelete - The delete album custom print mutation.
 * @returns {Promise<undefined>}
 */
const setAlbumCustomPrints = async options => {
  const {
    album,
    albumCustomPrintItems = [],
    albumCustomPrintCreate,
    albumCustomPrintUpdate,
    albumCustomPrintDelete,
  } = options;

  const albumCustomPrintsToDelete = get(
    album,
    'custom_prints.records',
    [],
  ).filter(
    albumCustomPrint =>
      !albumCustomPrintItems
        .map(({ customPrintId }) => customPrintId)
        .includes(albumCustomPrint.custom_print.id),
  );

  if (albumCustomPrintsToDelete.length > 0) {
    // Deleting the album custom prints:
    await Promise.all(
      albumCustomPrintsToDelete.map(albumCustomPrint =>
        albumCustomPrintDelete({ album_custom_print_id: albumCustomPrint.id }),
      ),
    );
  }

  if (albumCustomPrintItems.length > 0) {
    // Create/Update:
    await Promise.all(
      albumCustomPrintItems.map(albumCustomPrint => {
        const shippingPrice = get(albumCustomPrint, 'shipping');

        const albumCustomPrintForMutation = {
          price: {
            currency: CURRENCY.USD,
            value: albumCustomPrint.price,
          },
          discount_price: get(albumCustomPrint, 'discountPrice')
            ? {
                currency: CURRENCY.USD,
                value: albumCustomPrint.discountPrice,
              }
            : null,
          shipping_enabled:
            get(albumCustomPrint, 'shippingEnabled') ||
            (shippingPrice != null && shippingPrice > 0),
          shipping_price: get(albumCustomPrint, 'shipping')
            ? {
                currency: CURRENCY.USD,
                value: albumCustomPrint.shipping,
              }
            : null,
          shipping_required: !!get(albumCustomPrint, 'shippingRequired'),
        };

        if (albumCustomPrint.id) {
          // update:
          return albumCustomPrintUpdate({
            ...albumCustomPrintForMutation,
            album_custom_print_id: albumCustomPrint.id,
          });
        }
        // create
        return albumCustomPrintCreate({
          ...albumCustomPrintForMutation,
          album_id: album.id,
          custom_print_id: albumCustomPrint.customPrintId,
        });
      }),
    );
  }

  return undefined;
};

/**
 * Determines whether or not we should be displaying Face Highlights.
 *
 * @param {Object} opts The options.
 * @param {Album | AlbumFolder} opts.entity Either the album or albumFolder.
 * @param {Array<Identities>} opts.filteredIdentities List of filtered identities.
 * @param {enum<ALBUM_HIGHLIGHTS_FILTER>} opts.favoriteFilter Favorite photo filter.
 * @param {Number} opts.ratingFilter Filter by rating (usually greater than).
 */
export const shouldDisplayFaceHighlights = ({
  entity,
  filteredIdentities,
  favoriteFilter,
  ratingFilter,
}) => {
  return (
    !isAlbum(entity) ||
    filteredIdentities.length > 0 ||
    favoriteFilter !== ALBUM_HIGHLIGHTS_FILTER.ALL ||
    ratingFilter > 0
  );
};

/**
 * Given an album with the `album.selected_identities`
 * it produces the member (selected
 * identity) data required to populate the album members table using Roster identities.
 *
 * @param {object} album The album with its members as returned from GQL.
 * @returns {Array} The list of members.
 */
const getAlbumSelectedIdentityRowsWithRosterIds = album => {
  if (!album || !album.selected_identities) {
    return [];
  }

  const rows = album.selected_identities.records.map(selectedIdentity => {
    const rosterIdentity = selectedIdentity?.identity?.roster_identity;
    const identity = selectedIdentity?.identity ?? {};

    return {
      ...selectedIdentity,
      membershipId: selectedIdentity.album_membership.id,
      deleted: selectedIdentity.album_membership.is_deleted,
      identity: {
        ...selectedIdentity.identity,
        identityId: identity.id,
        current_matching_photo:
          getRosterIdentitySelfiePhoto(rosterIdentity) ||
          identity.current_matching_photo,
      },
    };
  });

  return rows;
};

/**
 * Gets relevant Uniform albumFolderTag information from the albumAlbumFolderTags resolver.
 *
 * @param {Object} albumAlbumFolderTags The albumAlbumFolderTags object.
 * @returns {Object} An object containing the albumFolderTag, related level, related jerseys, etc.
 *
 */
const getAlbumUniformTagItems = albumAlbumFolderTags => {
  const uniformAlbumAlbumFolderTag = getUniformAlbumAlbumFolderTag(
    albumAlbumFolderTags?.album_folder_tags?.records,
  );

  if (!uniformAlbumAlbumFolderTag) {
    return {
      albumFolderTag: undefined,
      albumUniformAlbumFolderTagLevel: undefined,
      uniformAlbumFolderTagLevel: undefined,
      uniformAlbumAlbumFolderTag: undefined,
      uniformAlbumFolderTagLevelJerseys: [],
    };
  }

  const albumFolderTag = uniformAlbumAlbumFolderTag?.album_folder_tag;

  const albumUniformAlbumFolderTagLevel = (uniformAlbumAlbumFolderTag
    ?.uniform_album_folder_tag_levels?.records ?? [])[0];

  const uniformAlbumFolderTagLevel =
    albumUniformAlbumFolderTagLevel?.uniform_album_folder_tag_level;

  const uniformAlbumFolderTagLevelJerseys =
    albumUniformAlbumFolderTagLevel?.uniform_album_folder_tag_level_jerseys
      ?.records ?? [];

  return {
    albumFolderTag,
    albumUniformAlbumFolderTagLevel,
    uniformAlbumFolderTagLevel,
    uniformAlbumAlbumFolderTag,
    uniformAlbumFolderTagLevelJerseys,
  };
};

const getAlbumDateLabel = album => {
  if (!album.start_date) {
    return 'N/A';
  }

  const startDate = moment(album.start_date).startOf('day');
  const endDate = moment(album.end_date).startOf('day');

  if (!album.end_date || endDate.diff(startDate) === 0) {
    return startDate.format(DATE_FORMAT_SIMPLE);
  }

  return `${startDate.format(DATE_FORMAT_SIMPLE)} ${
    album.end_date ? ` - ${endDate.format(DATE_FORMAT_SIMPLE)}` : ''
  }`;
};

/**
 * Returns whether the entity provided is considered a Weddings content item.
 *
 * @param {Object} entity The album or album folder.
 * @returns {boolean}
 */
const isWeddingsContent = entity => {
  // TODO: We should utilize this method in most places where we check for Weddings content.
  return (
    [ALBUM_TYPE.EVENT].includes(entity?.album_type) &&
    [ALBUM_SUBTYPE.WEDDINGS].includes(entity?.album_subtype)
  );
};

/**
 * Returns whether it is possible to upload to a given album
 *
 * @param {Object} album - The album.
 * @returns {boolean}
 */
const canUpload = album => {
  const albumFolder = album.root_album_folder ?? album.album_folder;

  return (
    !!albumFolder.upload_mode ||
    albumFolder.individual_albums ||
    isWeddingsContent(album) ||
    isWeddingsContent(albumFolder)
  );
};

const getDateLabel = album => {
  const startDate = album?.start_date ? new Date(album.start_date) : null;
  const endDate = album?.end_date ? new Date(album.end_date) : null;

  const startAndEndDateSameDay =
    !startDate || !endDate ? true : isSameDay(startDate, endDate);
  const dateFormat = 'MMMM do, yyyy';

  if (!startDate) {
    return '';
  }

  return `${format(startDate, dateFormat)} ${
    endDate && !startAndEndDateSameDay
      ? ` - ${format(endDate, dateFormat)}`
      : ''
  }`;
};

/**
 *  Returns whether the entity provided is considered PRO content or not
 *
 * @param {Object} entity the entity to check
 * @returns {boolean}
 */
const isProContent = entity => {
  return [ALBUM_TYPE.PRO, ALBUM_TYPE.CAMP].includes(entity?.album_type);
};

/**
 *  Returns whether the entity provided is considered consumer content or not
 *
 * @param {Object} entity the entity to check
 * @returns {boolean}
 */
const isConsumerEntity = entity => {
  return [
    ALBUM_TYPE.EVENT,
    ALBUM_TYPE.CONSUMER,
    ALBUM_TYPE.SHUTTERBUG,
  ].includes(entity?.album_type);
};

/**
 * Returns true for events that used the legacy purchased event APIs
 *
 * @param {Object} entity The album or albumFolder.
 * @returns {boolean}
 */
const isLegacyWeddingEvent = entity => {
  if (true) {
    // All weddings have been technically migrated.
    // This is not a relevant method anymore.
    return false;
  }

  return (
    isWeddingsContent(entity) &&
    (!!entity?.purchased_event ||
      !!entity.root_album_folder?.purchased_event) &&
    (entity?.purchased_event_addon_types?.length ?? 0) === 0 &&
    (entity?.root_album_folder?.purchased_event_addon_types?.length ?? 0) === 0
  );
};

const entityRequiresVGBContext = entity =>
  [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
    entity?.album_subtype,
  );

/**
 * Determines if the given album is the main photos album
 *
 * @param {Object} album the album to check
 * @param {Boolean} parentHasWaldofiedAddon whether or not the parent has a waldofied addon album already
 *
 * @returns {boolean}
 */
const isMainPhotosAlbum = (album, parentHasWaldofiedAddon = false) => {
  if (album.album_type !== ALBUM_TYPE.EVENT) {
    return false;
  }

  return (
    !!album?.is_wedding_day_album ||
    !!(
      !parentHasWaldofiedAddon &&
      album?.purchased_event_addon_types?.length === 0 &&
      album?.album_type === ALBUM_TYPE.EVENT &&
      album?.album_subtype !== ALBUM_SUBTYPE.WEDDINGS
    ) ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    ) ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
    ) ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
    )
  );
};

/**
 * Determines if the given album is Waldofied
 *
 * @param {Object} album the album to check
 *
 * @returns {boolean}
 */
const isWaldofied = album => {
  return (
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
    ) ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER,
    ) ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.WALDOFIED_PHOTO_FINDER_PLUS,
    )
  );
};

/**
 * Determines if the given album is a Memory Book album
 *
 * @param {Object} album the album to check
 *
 * @returns {boolean}
 */
const isMemoryBook = album => {
  return (
    !!album?.is_memory_book ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
    )
  );
};

/**
 * Determines if the given album is a VGB / GV album
 *
 * @param {Object} album the album to check
 *
 * @returns {boolean}
 */
const isVideoAlbum = album => {
  return (
    !!album?.is_video_album ||
    !!album?.purchased_event_addon_types?.includes(
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    )
  );
};

/**
 * Determines if a given album is a specialty album
 *
 * @param {Object} album the album to check
 *
 * @returns {boolean}
 */
const isSpecialtyAlbum = album => {
  return isMemoryBook(album) || isVideoAlbum(album);
};

/**
 * Determines if a given album uses VGB or GV context
 *
 * @param {Object} entity the entity to check
 *
 * @returns {boolean}
 */
const isVGBContext = entity => {
  return [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
    entity?.album_subtype,
  );
};

/**
 * Determines if the video slideshow is available to view automatically
 *
 * @param {Object} album the album to check
 *
 * @returns {boolean}
 */
const isVideoSlideshowAvailable = album => {
  if (
    !album?.slideshow ||
    album?.slideshow?.mode !== ALBUM_SLIDESHOW_MODE.ALL_VIDEOS
  ) {
    return false;
  }

  if (album.slideshow.status === ALBUM_SLIDESHOW_STATUS.DRAFT) {
    return false;
  }

  return true;
};

const isAutoLayoutsSlideshow = albumSlideshow => {
  return [
    ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
    ALBUM_SLIDESHOW_MODE.SELECTED_PHOTO_LAYOUTS,
  ].includes(albumSlideshow?.mode);
};

const isAllPhotosSlideshow = albumSlideshow => {
  return [
    ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
    ALBUM_SLIDESHOW_MODE.AUTO_LAYOUTS,
  ].includes(albumSlideshow?.mode);
};

const isSelectedPhotosSlideshow = albumSlideshow => {
  return [
    ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS,
    ALBUM_SLIDESHOW_MODE.SELECTED_PHOTO_LAYOUTS,
  ].includes(albumSlideshow?.mode);
};

export {
  isPpa,
  isPpd,
  hasFolderSubscription,
  joinUserAndMembershipAlbums,
  getAlbumTypeNameFromEnum,
  isAlbum,
  albumHasPhotoGroups,
  hasPpaPrice,
  allowsPhotoPurchase,
  allowsDownloadWatermarkOption,
  allowsPrintWatermarkOption,
  albumDateStatus,
  albumFilterByDateStatus,
  allowsFreeDownloads,
  socialShareEnabled,
  isAlbumPurchasable,
  isAlbumDiscoverable,
  getPhotoGroupsWithPhotos,
  getAlbumUniqueSelectedIdentityRows,
  getSelectedIdentityRowsFromSelectedIdentities,
  createAlbumRequiresPurchase,
  getGradeAlbumAlbumFolderTag,
  getUniformAlbumAlbumFolderTag,
  getCustomAlbumAlbumFolderTags,
  updateAlbumAlbumFolderGradeTag,
  updateUniformAlbumAlbumFolderTagUnique,
  hasUnconfiguredTeamJerseys,
  hasTeamTag,
  getTeamTag,
  updateCustomAlbumAlbumFolderTags,
  requiresConfirmationToDelete,
  requiresGreenScreenCheck,
  getAlbumAssignedPrintPackages,
  getAlbumUnassignedPrintPackages,
  getAlbumAssignedCustomPrints,
  getAlbumUnassignedCustomPrints,
  setAlbumCustomPrints,
  isAlbumPhotoGroup,
  getAlbumSelectedIdentityRowsWithRosterIds,
  getAlbumUniformTagItems,
  isAlbumIndividual,
  getAlbumDateLabel,
  canUpload,
  getDateLabel,
  isProContent,
  isConsumerEntity,
  isWeddingsContent,
  isLegacyWeddingEvent,
  entityRequiresVGBContext,
  isMainPhotosAlbum,
  isWaldofied,
  isMemoryBook,
  isVideoAlbum,
  isSpecialtyAlbum,
  isVGBContext,
  isVideoSlideshowAvailable,
  isAutoLayoutsSlideshow,
  isAllPhotosSlideshow,
  isSelectedPhotosSlideshow,
};
